import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import type { BillingSummary } from './types';

export const awsBillingApiRef = createApiRef<AwsBillingApi>({
  id: 'plugin.aws-billing.service',
});

export interface AwsBillingApi {
  getSummary(months: number): Promise<BillingSummary>;
  getMonthCosts(month: string): Promise<BillingSummary>;
  downloadCSV(month: string): Promise<Blob>;
  downloadHTML(month: string): Promise<Blob>;
}

export class AwsBillingClient implements AwsBillingApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('aws-billing');
  }

  private async throwIfError(resp: Response): Promise<void> {
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      throw new Error(body.error || `Error ${resp.status}: ${resp.statusText}`);
    }
  }

  async getSummary(months: number): Promise<BillingSummary> {
    const base = await this.baseUrl();
    const resp = await this.fetchApi.fetch(`${base}/billing/summary?months=${months}`);
    await this.throwIfError(resp);
    return resp.json() as Promise<BillingSummary>;
  }

  async getMonthCosts(month: string): Promise<BillingSummary> {
    const base = await this.baseUrl();
    const resp = await this.fetchApi.fetch(`${base}/billing/summary/month?month=${encodeURIComponent(month)}`);
    await this.throwIfError(resp);
    return resp.json() as Promise<BillingSummary>;
  }

  async downloadCSV(month: string): Promise<Blob> {
    const base = await this.baseUrl();
    const resp = await this.fetchApi.fetch(`${base}/billing/report/csv?month=${encodeURIComponent(month)}`);
    await this.throwIfError(resp);
    return resp.blob();
  }

  async downloadHTML(month: string): Promise<Blob> {
    const base = await this.baseUrl();
    const resp = await this.fetchApi.fetch(`${base}/billing/report/html?month=${encodeURIComponent(month)}`);
    await this.throwIfError(resp);
    return resp.blob();
  }
}
